import React, { useState } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { Button, LinkButton, StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { WarningCard } from '@eika/card';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Link } from '@eika/link';
import { RadioButtonPanels } from '@eika/radio-button';

import { useConfig } from '../../defaultConfig';
import type { Account } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import CenteredModalContent from '../shared/CenteredModalContent/CenteredModalContent';
import { ErrorCardWrapper } from '../shared/ErrorCardWrapper';

interface Props {
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    setTransferTo: React.Dispatch<React.SetStateAction<string>>;
    eligibleTransferAccounts: Account[];
    transferTo: string;
    onClose: () => void;
}

const CloseAccountForm: React.FC<Props> = ({
    onSubmit,
    setTransferTo,
    eligibleTransferAccounts,
    transferTo,
    onClose,
}) => {
    const translated = useTranslation();
    const { recurringPaymentLink } = useConfig();
    const [optionError, setOptionError] = useState(false);
    const [submitStage, setSubmitStage] = useState(false);
    const { signingStatus, signingOperationPending, handleCancelSigningFlow, signingError } = useSigningContext();

    const nextButton = () => {
        if (transferTo) {
            setSubmitStage(true);
        } else {
            setOptionError(true);
        }
    };

    const closeAndCancelSigning = () => {
        handleCancelSigningFlow();
        setSubmitStage(false);
        onClose();
    };

    const onChange = (value: string) => {
        setTransferTo(value);
        setOptionError(false);
    };

    if (signingStatus === 'COMPLETED') {
        return (
            <div className="close-account--success">
                <CheckmarkAnimation />
                <h2>{translated.closeAccount.success}</h2>
                <StandardButton onClick={onClose}>{translated.closeAccount.doneButton}</StandardButton>
            </div>
        );
    }

    if (signingError) {
        switch (signingError.errorName) {
            case 'ACCOUNT_CLOSE_FAILED_RECURRING_PAYMENT':
                return (
                    <WarningCard title={translated.closeAccount.closeRecurringPayment} isModal>
                        <p>{translated.closeAccount.closeRecurringPaymentDescription}</p>
                        <Link href={recurringPaymentLink?.url} linkType="button-positive">
                            {translated.closeAccount.closeRecurringPaymentLink}
                        </Link>
                    </WarningCard>
                );
            default:
                return (
                    <CenteredModalContent>
                        <ErrorCardWrapper exception={signingError} isModal>
                            <Button onClick={closeAndCancelSigning}>{translated.ok}</Button>
                        </ErrorCardWrapper>
                    </CenteredModalContent>
                );
        }
    }

    return (
        <SigningContextFlow onClosePayment={closeAndCancelSigning}>
            <form onSubmit={onSubmit} className="close-account__form">
                {submitStage ? (
                    <WarningCard title={translated.closeAccount.confirmTitle} isModal>
                        <p>{translated.closeAccount.confirmText}</p>
                        <div className="close-account__button-container">
                            <LinkButton onClick={() => setSubmitStage(false)}>
                                {translated.closeAccount.previousButton}
                            </LinkButton>
                            <ButtonWithSpinner
                                buttonType="positive"
                                showSpinner={signingOperationPending}
                                type="submit"
                            >
                                {translated.closeAccount.confirmButton}
                            </ButtonWithSpinner>
                        </div>
                    </WarningCard>
                ) : (
                    <>
                        <h2 className="typography-h1__light">{translated.closeAccount.title}</h2>
                        <RadioButtonPanels
                            label={translated.closeAccount.transferTo}
                            onChange={onChange}
                            options={eligibleTransferAccounts?.map((account) => {
                                return {
                                    value: account.accountNumber.value,
                                    label: account.alias,
                                    description: formatAccountNumber(account.accountNumber),
                                };
                            })}
                            selectedOption={transferTo}
                            errorMessage={optionError ? translated.closeAccount.chooseAccount : undefined}
                        />
                        <div className="close-account__button-container">
                            <StandardButton onClick={nextButton}>{translated.closeAccount.nextButton}</StandardButton>
                        </div>
                    </>
                )}
            </form>
        </SigningContextFlow>
    );
};

export default CloseAccountForm;
